/**
 * API Client with automatic JWT authentication
 *
 * Wraps fetch to automatically include the JWT token in Authorization header.
 * Handles 401 responses by clearing session and redirecting to login.
 * Use this for all authenticated API calls instead of raw fetch.
 */

// Get token from localStorage
const getToken = () => localStorage.getItem('token');

// Get the appropriate server address based on environment
const getServerAddress = () => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    return process.env.REACT_APP_BACKEND_URL
        || (isDevelopment ? 'http://localhost:3001/' : 'https://sb-backend-env-staging-scuttlebutt-research.vercel.app/');
};

// Handle 401 - clear stale auth and redirect to home
const handle401 = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('session');
    localStorage.removeItem('userDetails');
    sessionStorage.setItem('session_expired', 'true');
    window.location.href = '/';
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/user/bookmarks')
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>}
 */
export const apiRequest = async (endpoint, options = {}) => {
    const token = getToken();
    const baseUrl = getServerAddress();

    // Normalize endpoint to not have leading slash if baseUrl has trailing
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = baseUrl.endsWith('/') ? `${baseUrl}${normalizedEndpoint}` : `${baseUrl}/${normalizedEndpoint}`;

    console.log('[DEBUG] apiRequest:', { endpoint, baseUrl, url, hasToken: !!token });

    const headers = {
        ...options.headers,
    };

    // Add Authorization header if token exists
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
        ...options,
        headers,
    });

    // Handle auth failures - clear session and redirect to login
    if (response.status === 401) {
        handle401();
        throw new Error('Session expired');
    }

    return response;
};

/**
 * POST request with JSON body
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data
 * @returns {Promise<Response>}
 */
export const apiPost = (endpoint, data = {}) => {
    return apiRequest(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
};

/**
 * POST request with form data (URL-encoded)
 * @param {string} endpoint - API endpoint
 * @param {object} data - Form data as key-value pairs
 * @returns {Promise<Response>}
 */
export const apiPostForm = (endpoint, data = {}) => {
    return apiRequest(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data),
    });
};

/**
 * GET request with query parameters
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @returns {Promise<Response>}
 */
export const apiGet = (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const urlWithParams = queryString ? `${endpoint}?${queryString}` : endpoint;
    return apiRequest(urlWithParams, {
        method: 'GET',
    });
};

export default {
    request: apiRequest,
    post: apiPost,
    postForm: apiPostForm,
    get: apiGet,
};
